import { getAuthToken, useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useMemo } from "react";
import axios, { type Method, type AxiosResponse } from "axios";
import useSWR, { type SWRResponse } from "swr";
import { swrConfig, axiosRequestConfig, type Strategies, type axiosApiTypes } from "@/../config/swr.config";

interface Params {
  path: string;
  params?: any;
  method?: Method;
  data?: any;
  api?: axiosApiTypes;
  useJwt?: boolean;
  forceJwt?: boolean;
}

interface Config<R, D = any> {
  enabled?: boolean;
  strategy?: Strategies;
  select?: (data: D) => R;
}

type ReturnType<Data> = SWRResponse<Data> & {
  isLoading: boolean;
  isError: boolean;
};

const defaultSelect = <R, D>(data: D): R => data as unknown as R;

/**
 * @todo: add error processing
 */
export default function useApi<Return, ApiData>(
  { path, params, method = "GET", data, api = "v2", useJwt = false, forceJwt = true }: Params,
  { enabled = true, strategy = "cached", select = defaultSelect }: Config<Return, ApiData> = {}
): ReturnType<Return> {
  const { user } = useDynamicContext();

  const key = useMemo(() => {
    const preparedKey = enabled ? { method, path, params, data } : null;
    if (useJwt && forceJwt && !user) {
      return null;
    }
    return preparedKey;
  }, [enabled, method, path, params, data, useJwt, forceJwt, user]);

  const res = useSWR<Return>(
    key,
    params =>
      axios
        .request({
          ...axiosRequestConfig[api],
          baseURL: axiosRequestConfig[api].baseURL,
          url: params.path,
          method: params.method,
          params: params.params,
          data: params.data,
          headers: {
            ...axiosRequestConfig[api].headers,
            ...(useJwt ? { "SF-JWT": getAuthToken() } : {}),
          },
          paramsSerializer: {
            indexes: null,
          },
        })
        .then((res: AxiosResponse<ApiData>) => res.data)
        .then(select),
    swrConfig[strategy]
  );

  return useMemo(() => ({ ...res, isLoading: res.isLoading || res.isValidating, isError: Boolean(res.error) }), [res]);
}
